/**
 * Iter 229 · Timesheet Settings (workspace default weekly target + per-employee overrides).
 * Rendered inside the /app/settings page under the "Timesheet" tab.
 *
 * Owner/admin only — the backend enforces this and will 403 otherwise, so
 * we defensively hide the block for non-admins to avoid an ugly toast.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Save, Loader2, Clock3, User, RotateCcw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { apiErrorMessage } from "@/lib/apiError";
import { useEffortFormat } from "@/lib/effortFormat";

export default function TimesheetSettingsSection() {
  const { activeWorkspaceId, activeWorkspace, user } = useAuth();
  const role = activeWorkspace?.role || activeWorkspace?.membership_role;
  const isAdmin = user?.is_super_admin || role === "owner" || role === "admin";

  const [wsTarget, setWsTarget] = useState("");
  const [savedWsTarget, setSavedWsTarget] = useState(null);
  const [wsDaily, setWsDaily] = useState("");
  const [savedWsDaily, setSavedWsDaily] = useState(null);
  // Feb 2026 · Effort rounding digits (0..3, default 2). Also patched
  // into the shared EffortFormatProvider so every open surface updates
  // immediately without a page refresh.
  const [wsRounding, setWsRounding] = useState("2");
  const [savedWsRounding, setSavedWsRounding] = useState(2);
  const [savingWsRounding, setSavingWsRounding] = useState(false);
  const { setDigits: setEffortDigits, formatHours: fmtPreview } = useEffortFormat();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingWs, setSavingWs] = useState(false);
  const [savingWsDaily, setSavingWsDaily] = useState(false);
  const [savingEmp, setSavingEmp] = useState(null);   // emp id currently saving
  const [savingEmpDaily, setSavingEmpDaily] = useState(null);
  const [edits, setEdits] = useState({});             // eid -> string value (weekly)
  const [dailyEdits, setDailyEdits] = useState({});   // eid -> string value (daily)

  const load = async () => {
    if (!activeWorkspaceId || !isAdmin) return;
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/settings/timesheet`),
        api.get(`/workspaces/${activeWorkspaceId}/timesheet/team-targets`),
      ]);
      setWsTarget(String(s.data?.weekly_target_hours ?? 40));
      setSavedWsTarget(Number(s.data?.weekly_target_hours ?? 40));
      setWsDaily(String(s.data?.min_hours_per_day ?? 8));
      setSavedWsDaily(Number(s.data?.min_hours_per_day ?? 8));
      const rd = Number.isFinite(Number(s.data?.effort_rounding_digits)) ? Number(s.data?.effort_rounding_digits) : 2;
      setWsRounding(String(rd));
      setSavedWsRounding(rd);
      setMembers(Array.isArray(t.data) ? t.data : []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load timesheet settings"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, isAdmin]);

  const saveWs = async () => {
    setSavingWs(true);
    try {
      const { data } = await api.put(`/workspaces/${activeWorkspaceId}/settings/timesheet`,
                                          { weekly_target_hours: Number(wsTarget) });
      setSavedWsTarget(Number(data?.weekly_target_hours ?? 40));
      toast.success("Workspace default saved");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSavingWs(false); }
  };

  const saveWsDaily = async () => {
    setSavingWsDaily(true);
    try {
      const { data } = await api.put(`/workspaces/${activeWorkspaceId}/settings/timesheet`,
                                          { min_hours_per_day: Number(wsDaily) });
      setSavedWsDaily(Number(data?.min_hours_per_day ?? 8));
      toast.success("Daily minimum saved");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSavingWsDaily(false); }
  };

  const saveWsRounding = async () => {
    setSavingWsRounding(true);
    try {
      const nextDigits = Math.max(0, Math.min(3, Number(wsRounding)));
      const { data } = await api.put(`/workspaces/${activeWorkspaceId}/settings/timesheet`,
                                          { effort_rounding_digits: nextDigits });
      const saved = Number.isFinite(Number(data?.effort_rounding_digits)) ? Number(data?.effort_rounding_digits) : 2;
      setSavedWsRounding(saved);
      // Push into the shared provider so every open Daily-Plan / list /
      // report re-renders with the new precision without a page reload.
      setEffortDigits?.(saved);
      toast.success(`Effort rounding set to ${saved} decimals`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSavingWsRounding(false); }
  };

  const saveEmp = async (emp, valueRaw) => {
    setSavingEmp(emp.id);
    try {
      const v = valueRaw === "" ? null : Number(valueRaw);
      const { data } = await api.patch(`/employees/${emp.id}/timesheet-target`,
                                            { weekly_target_hours: v });
      setMembers((prev) => prev.map((m) => m.id === emp.id ? { ...m, weekly_target_hours: data?.weekly_target_hours } : m));
      setEdits((e) => { const c = { ...e }; delete c[emp.id]; return c; });
      toast.success(v ? `Set ${emp.full_name} to ${v}h/week` : `Reset ${emp.full_name} to workspace default`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSavingEmp(null); }
  };

  const saveEmpDaily = async (emp, valueRaw) => {
    setSavingEmpDaily(emp.id);
    try {
      const v = valueRaw === "" ? null : Number(valueRaw);
      const { data } = await api.patch(`/employees/${emp.id}/timesheet-target`,
                                            { min_hours_per_day: v });
      setMembers((prev) => prev.map((m) => m.id === emp.id ? { ...m, min_hours_per_day: data?.min_hours_per_day } : m));
      setDailyEdits((e) => { const c = { ...e }; delete c[emp.id]; return c; });
      toast.success(v ? `Set ${emp.full_name} min ${v}h/day` : `Reset ${emp.full_name} daily to workspace default`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSavingEmpDaily(null); }
  };

  if (!isAdmin) {
    return (
      <div className="border border-border p-6 text-sm text-muted-foreground" data-testid="settings-timesheet-forbidden">
        Only workspace owners/admins can view or change timesheet targets.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-timesheet-section">
      {/* Workspace defaults — weekly target + daily minimum */}
      <div className="border border-border p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Clock3 className="w-4 h-4 text-primary" />
          <div className="label-eyebrow">Workspace defaults</div>
        </div>
        <p className="text-sm text-muted-foreground -mt-2">
          The default targets applied to every employee in this workspace. The
          <span className="font-mono"> weekly </span> target drives the header
          <span className="font-mono"> Xh / Yh </span> chip from Wednesday onwards.
          The <span className="font-mono">daily minimum</span> is used by the Advanced
          Timesheet <span className="italic">short-day</span> exception flag — anyone
          logging less than this on a working day gets flagged. Set 0 to disable flagging.
          Individual employees can be overridden in the table below.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-40">
            <Label className="label-eyebrow mb-2 inline-block">Weekly (h/week)</Label>
            <Input
              type="number" min="1" max="168" step="0.5"
              value={wsTarget}
              onChange={(e) => setWsTarget(e.target.value)}
              className="h-10 font-mono"
              data-testid="settings-ts-ws-target-input"
            />
          </div>
          <Button
            onClick={saveWs}
            disabled={savingWs || Number(wsTarget) === savedWsTarget || !wsTarget}
            className="h-10 rounded-none"
            data-testid="settings-ts-ws-target-save"
          >
            {savingWs ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
          {savedWsTarget !== null && (
            <div className="text-xs text-muted-foreground pb-2.5">
              Current: <span className="font-mono font-semibold">{savedWsTarget}h/week</span>
            </div>
          )}
          <div className="w-px h-10 bg-border mx-2 hidden md:block" />
          <div className="w-40">
            <Label className="label-eyebrow mb-2 inline-block">Daily min (h/day)</Label>
            <Input
              type="number" min="0" max="24" step="0.5"
              value={wsDaily}
              onChange={(e) => setWsDaily(e.target.value)}
              className="h-10 font-mono"
              data-testid="settings-ts-ws-daily-input"
            />
          </div>
          <Button
            onClick={saveWsDaily}
            disabled={savingWsDaily || Number(wsDaily) === savedWsDaily || wsDaily === ""}
            className="h-10 rounded-none"
            data-testid="settings-ts-ws-daily-save"
          >
            {savingWsDaily ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
          {savedWsDaily !== null && (
            <div className="text-xs text-muted-foreground pb-2.5">
              Current: <span className="font-mono font-semibold">{savedWsDaily}h/day</span>
            </div>
          )}
        </div>
        {/* Feb 2026 · Effort rounding — governs every UI surface that
            renders decimal hours. Users complained day + month totals
            disagreed; the fix is displaying with one consistent digit
            count across every surface. */}
        <div className="flex flex-wrap items-end gap-3 pt-4 border-t border-border" data-testid="settings-ts-rounding-wrap">
          <div className="w-56">
            <Label className="label-eyebrow mb-2 inline-block">Effort rounding</Label>
            <Select value={wsRounding} onValueChange={setWsRounding}>
              <SelectTrigger className="h-10 rounded-none font-mono" data-testid="settings-ts-rounding-input">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">0 decimals · 8h</SelectItem>
                <SelectItem value="1">1 decimal · 8.3h</SelectItem>
                <SelectItem value="2">2 decimals · 8.25h (default)</SelectItem>
                <SelectItem value="3">3 decimals · 8.250h</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={saveWsRounding}
            disabled={savingWsRounding || Number(wsRounding) === savedWsRounding}
            className="h-10 rounded-none"
            data-testid="settings-ts-rounding-save"
          >
            {savingWsRounding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
          <div className="text-xs text-muted-foreground pb-2.5">
            Preview: <span className="font-mono font-semibold" data-testid="settings-ts-rounding-preview">
              {fmtPreview(8.253, { unit: true }).replace(/[^\d.]/g, (m) => m)}
            </span>
            {" "}
            <span className="ml-3">Current: <span className="font-mono font-semibold">{savedWsRounding} decimal{savedWsRounding === 1 ? "" : "s"}</span></span>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground -mt-1">
          Applied everywhere hours are shown: Daily Plan (day/week/month/list), Timesheet reports, task hours, People Analytics.
          Values are always <span className="italic">summed unrounded</span> internally and only rounded at display — so day and monthly totals will now match to the last decimal.
        </p>
      </div>

      {/* Per-employee overrides */}
      <div className="border border-border p-6 space-y-4">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-primary" />
          <div className="label-eyebrow">Per-employee overrides</div>
        </div>
        <p className="text-sm text-muted-foreground -mt-2">
          Set custom weekly + daily targets for part-time or contract employees.
          Leave a field blank to fall back to the workspace default
          (or <span className="font-mono">20h/week</span> for anyone marked
          <span className="italic"> part_time</span> with no override).
        </p>
        {loading ? (
          <div className="text-sm text-muted-foreground py-8 text-center">Loading employees…</div>
        ) : members.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border">
            No employees yet.
          </div>
        ) : (
          <div className="max-h-[480px] overflow-y-auto border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b border-border sticky top-0">
                <tr className="text-left text-[10px] uppercase text-muted-foreground">
                  <th className="px-3 py-2">Employee</th>
                  <th className="px-3 py-2 w-32">Type</th>
                  <th className="px-3 py-2 w-40">Weekly (h/week)</th>
                  <th className="px-3 py-2 w-20 text-right"></th>
                  <th className="px-3 py-2 w-40">Daily min (h/day)</th>
                  <th className="px-3 py-2 w-20 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {members.map((emp) => {
                  const currentOverride = emp.weekly_target_hours;
                  const editing = edits[emp.id];
                  const val = editing !== undefined ? editing : (currentOverride ?? "");
                  const changed = String(val) !== String(currentOverride ?? "");
                  const currentDaily = emp.min_hours_per_day;
                  const editingDaily = dailyEdits[emp.id];
                  const dailyVal = editingDaily !== undefined ? editingDaily : (currentDaily ?? "");
                  const dailyChanged = String(dailyVal) !== String(currentDaily ?? "");
                  return (
                    <tr key={emp.id} data-testid={`settings-ts-emp-row-${emp.id}`}>
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium">{emp.full_name || emp.email || "—"}</div>
                        <div className="text-[10px] text-muted-foreground">{emp.email || emp.department || ""}</div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 border ${emp.employment_type === "part_time" ? "bg-amber-50 text-amber-700 border-amber-300" : "bg-muted text-muted-foreground border-border"}`}>
                          {emp.employment_type || "full_time"}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <Input
                          type="number" min="0" max="168" step="0.5"
                          value={val}
                          onChange={(e) => setEdits((es) => ({ ...es, [emp.id]: e.target.value }))}
                          placeholder={`(default ${savedWsTarget ?? 40})`}
                          className="h-8 font-mono"
                          data-testid={`settings-ts-emp-input-${emp.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 align-top text-right">
                        <div className="flex items-center gap-1 justify-end">
                          {changed && (
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => saveEmp(emp, val)}
                              disabled={savingEmp === emp.id}
                              className="rounded-none h-7 text-[10px] px-2"
                              data-testid={`settings-ts-emp-save-${emp.id}`}
                            >
                              {savingEmp === emp.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            </Button>
                          )}
                          {currentOverride != null && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => saveEmp(emp, "")}
                              disabled={savingEmp === emp.id}
                              className="rounded-none h-7 text-[10px] px-2"
                              data-testid={`settings-ts-emp-reset-${emp.id}`}
                              title="Reset to workspace default"
                            >
                              <RotateCcw className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <Input
                          type="number" min="0" max="24" step="0.5"
                          value={dailyVal}
                          onChange={(e) => setDailyEdits((es) => ({ ...es, [emp.id]: e.target.value }))}
                          placeholder={`(default ${savedWsDaily ?? 8})`}
                          className="h-8 font-mono"
                          data-testid={`settings-ts-emp-daily-input-${emp.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 align-top text-right">
                        <div className="flex items-center gap-1 justify-end">
                          {dailyChanged && (
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => saveEmpDaily(emp, dailyVal)}
                              disabled={savingEmpDaily === emp.id}
                              className="rounded-none h-7 text-[10px] px-2"
                              data-testid={`settings-ts-emp-daily-save-${emp.id}`}
                            >
                              {savingEmpDaily === emp.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            </Button>
                          )}
                          {currentDaily != null && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => saveEmpDaily(emp, "")}
                              disabled={savingEmpDaily === emp.id}
                              className="rounded-none h-7 text-[10px] px-2"
                              data-testid={`settings-ts-emp-daily-reset-${emp.id}`}
                              title="Reset to workspace default"
                            >
                              <RotateCcw className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
